import axios from 'axios';
import { getFieldErrors } from './FormErrors';
import { isRouteId } from './FormValidation';
import { REQUEST_TIMEOUT_MS } from './RequestGuard';

interface Observer {
    validate(): Promise<boolean> | boolean;
    setErrors(errors: Record<string, string[]>): void;
}
export interface SaveContext {
    saving: boolean;
    disposed: boolean;
    requestVersion: number;
    responseError: boolean;
    created?: boolean;
    updated?: boolean;
    $refs: Record<string, Observer | undefined>;
    $t(message: string): unknown;
    $emit(event: string, value: boolean): void;
}
interface SaveOptions {
    method: 'put' | 'post';
    url: string;
    payload: Record<string, unknown>;
    observer: string;
    success: 'created' | 'updated';
    event: string;
    fields: readonly string[];
    fieldAliases?: Readonly<Record<string, string>>;
    validPayload(payload: Record<string, unknown>): boolean;
    validResponse?(data: unknown): boolean;
}
interface HttpFailure { response?: { status?: number; data?: unknown } }

/** Shared lifecycle for forms whose backend returns a created/updated numeric id. */
export async function saveForm(context: SaveContext, options: SaveOptions): Promise<void> {
    if (context.disposed || context.saving) return;
    const observer = context.$refs[options.observer];
    if (!observer || typeof observer.validate !== 'function') return;
    const request = ++context.requestVersion;
    context.saving = true; context.responseError = false; context[options.success] = false;
    try {
        const payload: Record<string, unknown> = JSON.parse(JSON.stringify(options.payload));
        observer.setErrors({});
        const valid = await observer.validate();
        if (context.disposed || request !== context.requestVersion || !valid) return;
        if (!options.validPayload(payload)) { context.responseError = true; return; }
        const response = await axios.request({ method: options.method, url: options.url, data: payload,
            timeout: REQUEST_TIMEOUT_MS, headers: { 'Content-type': 'application/json' } });
        const data: unknown = response.data;
        const success = options.validResponse ? options.validResponse(data)
            : data !== null && typeof data === 'object' && isRouteId((data as { id?: unknown }).id);
        if (!success) throw new Error('Invalid form save response');
        if (context.disposed || request !== context.requestVersion) return;
        context[options.success] = true; context.$emit(options.event, true);
    } catch (error) {
        const failure = error as HttpFailure | null | undefined;
        if (context.disposed || request !== context.requestVersion || failure?.response?.status === 401) return;
        context.responseError = true;
        if (failure?.response?.status === 400) {
            const source = getFieldErrors(failure.response.data, [...options.fields, ...Object.keys(options.fieldAliases || {})]);
            const fields: Record<string, string[]> = {};
            for (const key of Object.keys(source)) {
                const target = options.fieldAliases?.[key] || key;
                if (!options.fields.includes(target)) continue;
                fields[target] = [...(fields[target] || []), ...source[key].map(message => String(context.$t(message)))];
            }
            observer.setErrors(fields);
        }
    } finally { if (!context.disposed && request === context.requestVersion) context.saving = false; }
}
